import autoBind from "auto-bind";
import {proxy} from "valtio";

import {CalendarDay, DateRangePickerClass, DateRangePickerConfig} from "./daterange-picker";

export interface MonthData {
    month: number;
    year: number;
    monthName: string;
    days: Array<CalendarDay>;
}

interface ScrollableDateRangePickerConfig extends DateRangePickerConfig {
    /** Initial number of months to display (default: 3) */
    initialMonthsCount?: number;
    /** Number of months to load when "load more" is clicked (default: 3) */
    loadMoreCount?: number;
}

class ScrollableDateRangePickerClass extends DateRangePickerClass {
    // Reactive state for scrollable view
    _visibleMonthsCount: number;

    // Non-reactive config
    private readonly loadMoreCount: number;

    constructor(config: ScrollableDateRangePickerConfig = {}) {
        super(config);

        this._visibleMonthsCount = config.initialMonthsCount ?? 3;
        this.loadMoreCount = config.loadMoreCount ?? 3;
    }

    /**
     * Get array of months with their calendar days for scrollable view
     * Starts from current view month and extends for _visibleMonthsCount months
     */
    get scrollableMonths(): Array<MonthData> {
        const months: Array<MonthData> = [];

        let currentMonth = this._viewMonth;
        let currentYear = this._viewYear;

        for (let i = 0; i < this._visibleMonthsCount; i++) {
            const monthName = this.formatMonthYear(new Date(currentYear, currentMonth));

            months.push({
                month: currentMonth,
                year: currentYear,
                monthName,
                days: this.getCalendarDaysForMonth(currentMonth, currentYear),
            });

            // Move to next month
            if (currentMonth === 11) {
                currentMonth = 0;
                currentYear += 1;
            } else {
                currentMonth += 1;
            }
        }

        return months;
    }

    /**
     * Get only the days of each month (without padding from prev/next months)
     * Better for mobile scroll view
     */
    get scrollableMonthsCompact(): Array<MonthData> {
        const months: Array<MonthData> = [];

        let currentMonth = this._viewMonth;
        let currentYear = this._viewYear;

        for (let i = 0; i < this._visibleMonthsCount; i++) {
            const monthName = this.formatMonthYear(new Date(currentYear, currentMonth));

            // Get only current month days (filter out padding days)
            const allDays = this.getCalendarDaysForMonth(currentMonth, currentYear);
            const currentMonthDays = allDays.filter((day) => day.isCurrentMonth);

            months.push({
                month: currentMonth,
                year: currentYear,
                monthName,
                days: currentMonthDays,
            });

            // Move to next month
            if (currentMonth === 11) {
                currentMonth = 0;
                currentYear += 1;
            } else {
                currentMonth += 1;
            }
        }

        return months;
    }

    /**
     * Number of currently visible months
     */
    get visibleMonthsCount(): number {
        return this._visibleMonthsCount;
    }

    /**
     * Check if there are more months that can be loaded
     * (limit to 12 months ahead)
     */
    get canLoadMore(): boolean {
        return this._visibleMonthsCount < 12;
    }

    /**
     * Load more months for scrollable view
     */
    loadMoreMonths(count?: number): void {
        const toAdd = count ?? this.loadMoreCount;
        this._visibleMonthsCount = Math.min(this._visibleMonthsCount + toAdd, 12);
    }

    /**
     * Handle day hover for scrollable calendar view
     * Sets hover date if day is not disabled
     */
    handleDayHover(day: CalendarDay, isDisabled: boolean): void {
        if (!isDisabled) {
            this.setHoverDate(day.date);
        }
    }

    /**
     * Reset visible months count to initial value
     */
    resetVisibleMonths(count = 3): void {
        this._visibleMonthsCount = count;
    }

    /**
     * Override reset to also reset visible months
     */
    reset(): void {
        super.reset();
        this._visibleMonthsCount = 3;
    }
}

// Export type
export type ScrollableDateRangePickerInstance = ScrollableDateRangePickerClass;

// Factory function (SSR compatible)
export function ScrollableDateRangePicker(config?: ScrollableDateRangePickerConfig): ScrollableDateRangePickerInstance {
    return autoBind(proxy<ScrollableDateRangePickerInstance>(new ScrollableDateRangePickerClass(config)));
}
